<template>
  <dialog ref="ssoSearchModal">
    <div v-if="show" class="modal active" :id="ssoSearchModalId">
      <div class="modal-overlay"></div>
      <div class="modal-container" v-if="!loginLoading">
        <div class="modal-header">
          <button
            class="btn btn-clear float-right"
            aria-label="Close"
            @click="CloseModal"></button>
          <div class="modal-title">Sign in with partner organization</div>
        </div>
        <div class="modal-body">
          <label class="form-label" for="partner-selection"
            >Search for your organization</label
          >
          <combo-box
            :autocomplete="fetchIdps"
            :clearable="true"
            :defaultId="null"
            :dontFocus="false"
            :embedList="mobileView"
            :fixed="true"
            :force="false"
            :height="40"
            inputId="partner-selection"
            :itemName="(idp) => idp.name"
            :itemId="(idp) => idp.entity_id"
            :parentEleId="ssoSearchModalId"
            :placeholder="'Search for your organization'"
            :placeholderEmpty="'Start typing to search for your organization'"
            :requiresValue="false"
            :width="mobileView ? 29 : 50"
            @emitError="(err) => $emit('emitError', err)"
            @input="(val) => (selected = val)">
          </combo-box>
          <transition name="fade" mode="out-in">
            <div
              v-if="selected && selected.id !== 'none'"
              :style="{
                width: mobileView ? '29rem' : '50rem',
              }">
              <div
                v-if="selected.logos && selected.logos.length > 0"
                class="logo">
                <img
                  :src="selected.logos[selected.logos.length - 1]"
                  :alt="selected.name"
                  style="max-width: 100%; max-height: 100px" />
              </div>
            </div>
          </transition>
        </div>
        <div class="modal-footer">
          <div class="form-group" style="float: left">
            <label class="form-checkbox remember-me-switch">
              <input type="checkbox" v-model="saveSession" />
              <i class="form-icon"></i> Remember me
            </label>
          </div>
          <div class="bottom-item-buttons">
            <button
              @click="doExternalLogin"
              class="btn btn-primary"
              :class="{ loading }"
              style="width: 10em"
              :disabled="
                !selected || (selected && selected.id === 'none') || null
              ">
              Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  </dialog>
</template>

<script>
import _ from 'lodash';
import { Buffer } from 'buffer/';
import moment from 'moment';
import pako from 'pako';

import auth from 'assets/js/auth';
import SSO from 'assets/js/auth/sso';
import { Get, urls } from 'assets/js/api';
import { SAML_URL } from 'assets/js/api/environment';

import ComboBox from 'components/Widgets/ComboBox/ComboBox';

const endpoints = {
  canarie: {
    ACSURL: 'https://saml.qreserve.com/sso/canarie',
    ACSURLTesting: 'https://samltesting.qreserve.com/api/saml/sso/canarie',
    entityID: 'https://saml.qreserve.com/shibboleth-sp',
    entityIDTesting: 'https://samltesting.qreserve.com/api/saml/shibboleth-sp',
  },
  google: {
    ACSURL: 'https://saml.qreserve.com/sso/google',
    ACSURLTesting: 'https://samltesting.qreserve.com/api/saml/sso/google',
    entityID: 'https://saml.qreserve.com/google-sp',
    entityIDTesting: 'https://samltesting.qreserve.com/api/saml/google-sp',
  },
};

const samlTestingDomain = 'samltesting.qreserve.com';

export default {
  name: 'partner-modal',

  components: {
    ComboBox,
  },

  computed: {},

  created() {
    const samlUrlHost = this.extractHostname(SAML_URL);
    if (samlUrlHost === samlTestingDomain) this.testingEnv = true;
  },

  data() {
    return {
      loading: false,
      saveSession: Boolean(window.stayLoggedIn),
      selected: null,
      show: false,
      ssoSearchModalId: 'sso-search-modal',
      testingEnv: false,
    };
  },

  emits: ['emitError'],

  methods: {
    // doExternalLogin generates a SAML authnrequest and sends it off to
    // an IDP so that the user can login through them.
    async doExternalLogin() {
      this.loading = true;

      // If there's an upstream entity ID set, just forward us to this
      // entity after pulling it down from the entity database.
      if (this.entityIdAutoLogin) {
        try {
          const idps = await this.fetchIdps(this.entityIdAutoLogin);
          this.selected = idps[0];
        } catch (err) {
          this.$emit('emitError', err);
          this.loading = false;
          return;
        }
      }

      if (!this.selected || this.selected.id === 'none') return;
      this.rememberIdp(this.selected);

      if (this.entityIdAutoLogin) {
        this.CloseModal();
      }

      // Google passes extra URL params.
      let joiner = '?';
      if (
        this.extractRootDomain(this.selected.sign_on_redirect) === 'google.com'
      ) {
        joiner = '&';
      }

      const samlQueryParams = await this.generateSAMLRequest();

      const url =
        `${this.selected.sign_on_redirect}` +
        `${joiner}SAMLRequest=${samlQueryParams}`;

      // If we are in an iframe, post the message to the parent.
      // We limit where we can be embedded so this CSP policy helps protect us.
      if (window !== window.parent) {
        const msg = {
          name: 'SAMLRequestUrl',
          value: url,
        };
        // Turn off loading so users can try again if it fails.
        this.loading = false;
        window.parent.postMessage(msg, '*');
      } else {
        window.location.href = url;
      }

      // Don't unset the loading state here, as the browser should be
      // redirecting.
    },

    // extractHostname extracts the hostname of a URL, e.g.
    // foo.google.com/whatever becomes foo.google.com.
    extractHostname(url) {
      let hostname;
      // find & remove protocol (http, ftp, etc.) and get hostname
      if (!url) return '';

      if (url.indexOf('://') > -1) {
        hostname = url.split('/')[2];
      } else {
        hostname = url.split('/')[0];
      }

      // find & remove port number
      hostname = hostname.split(':')[0];
      // find & remove "?"
      hostname = hostname.split('?')[0];

      return hostname;
    },

    // extractRootDomain returns the root domain of a given URL, e.g.
    // foo.google.com becomes google.com.
    extractRootDomain(url) {
      let domain = this.extractHostname(url);
      const splitArr = domain.split('.');
      const arrLen = splitArr.length;

      // extracting the root domain here
      // if there is a subdomain
      if (arrLen > 2) {
        domain = `${splitArr[arrLen - 2]}.${splitArr[arrLen - 1]}`;
        // check to see if it's using a Country Code Top Level Domain
        // (ccTLD) (i.e. ".me.uk")
        if (
          splitArr[arrLen - 1].length === 2 &&
          splitArr[arrLen - 1].length === 2
        ) {
          // this is using a ccTLD
          domain = `${splitArr[arrLen - 3]}.${domain}`;
        }
      }
      return domain;
    },

    // fetchIdps fetches the list of IDPs from the backend, with an optional
    // search string.
    async fetchIdps(search) {
      let respCanarie;
      let respGoogle;
      try {
        const promiseCanarie = async () => {
          respCanarie = await Get(urls.IDP_SEARCH('canarie', search));
        };
        const promiseGoogle = async () => {
          respGoogle = await Get(urls.IDP_SEARCH('google', search));
        };
        await Promise.all([promiseCanarie(), promiseGoogle()]);
      } catch (err) {
        this.$emit('emitError', err);
      }

      if (respCanarie && respGoogle) {
        // Concatenate the two arrays of results and inject a single name.
        const withNames = _.map(
          _.concat(respCanarie.data, respGoogle.data),
          (ele) => _.extend({}, ele, { name: this.idpNameGenerate(ele) }),
        );
        return withNames.sort(this.sortByFieldNameFunc('name'));
      }
      return [];
    },

    // generateSAMLRequest generates a SAML request for an SP-initiated
    // authentication by an IDP.
    async generateSAMLRequest() {
      if (!this.selected || this.selected.id === 'none') return '';

      // Make a random request ID.
      const id = `QRESERVE_AUTH_REQ_${this.uuid4()}`;

      let entityID = this.testingEnv
        ? endpoints.canarie.entityIDTesting
        : endpoints.canarie.entityID;
      let ssoEndpoint = this.testingEnv
        ? endpoints.canarie.ACSURLTesting
        : endpoints.canarie.ACSURL;
      if (
        this.extractRootDomain(this.selected.sign_on_redirect) === 'google.com'
      ) {
        entityID = this.testingEnv
          ? endpoints.google.entityIDTesting
          : endpoints.google.entityID;
        ssoEndpoint = this.testingEnv
          ? endpoints.google.ACSURLTesting
          : endpoints.google.ACSURL;
      }

      // We need server time because we know that to be true. Some users
      // have incorrect local times.
      let serverTimestamp;
      try {
        const resp = await Get(urls.TIME_SERVER);
        serverTimestamp = resp.data;
      } catch (err) {
        this.$emit('emitError', err);
        return null;
      }

      const serverTimeStr = moment
        .unix(serverTimestamp)
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss');
      const dt = `${serverTimeStr}Z`;
      const str =
        '<samlp:AuthnRequest xmlns:samlp="urn:oasis:names:tc:' +
        'SAML:2.0:protocol" AssertionConsumerServiceURL="' +
        `${ssoEndpoint}" Destination="` +
        `${this.selected.sign_on_redirect}" ID="${id}" IssueInstant="` +
        `${dt}" ProtocolBinding="urn:oasis:names:tc:SAML:2.0:bindings:` +
        'HTTP-POST" Version="2.0"><saml:Issuer xmlns:saml="urn:oasis:' +
        `names:tc:SAML:2.0:assertion">${entityID}` +
        '</saml:Issuer><samlp:NameIDPolicy AllowCreate="1"/></samlp:' +
        'AuthnRequest>';
      const buf = Buffer.from(str, 'utf8');
      const rawDeflated = pako.deflateRaw(buf);
      const enc = Buffer.from(rawDeflated).toString('base64');

      return encodeURIComponent(enc);
    },

    // idpNameGenerate generates a single name for a given IDP.
    idpNameGenerate(idp) {
      if (!idp.names || (idp.names && idp.names.length === 0)) {
        return 'Unknown Entity';
      }
      let domain = this.extractRootDomain(idp.entity_id);
      const regex = /[a-zA-Z]/;

      if (!regex.test(domain)) {
        // No letters so must just be an IP address; hide it.
        domain = '';
      }
      // Check for very common generic IDPs that don't help.
      if (
        ['okta.com', 'google.com', 'windows.net', 'xecurify.com'].indexOf(
          domain,
        ) !== -1
      ) {
        domain = '';
      }
      if (domain !== '') {
        domain = ` (${domain})`;
      }

      if (idp.names && idp.names.length === 1) {
        return `${idp.names[0]}${domain}`;
      }
      return `${idp.names[1]}${domain}`;
    },

    rememberIdp(idp) {
      window.localStorage.setItem(SSO.DefaultEntityKey, JSON.stringify(idp));
    },

    CloseModal() {
      if (_.isFunction(_.get(this, '$refs.ssoSearchModal.close'))) {
        this.$refs.ssoSearchModal.close();
      }
      this.show = false;
    },
    ShowModal() {
      this.show = true;
      if (this.$refs.ssoSearchModal) {
        this.$refs.ssoSearchModal.showModal();
      }
      if (this.afterLoaded) this.afterLoaded();
    },
  },

  mounted() {},

  props: {
    // Booleans
    loginLoading: {
      type: Boolean,
      required: true,
    },
    mobileView: {
      type: Boolean,
      required: true,
    },

    // Functions
    afterLoaded: {
      type: Function,
    },

    // Strings
    entityIdAutoLogin: {
      type: String,
      required: false,
    },
  },

  watch: {
    saveSession: {
      handler: function handle(val) {
        auth.StayLoggedInStore(val);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~assets/css/colors';
@import '~assets/css/buttons';
@import '~assets/css/partials/disable-modal-animate';

a:focus {
  box-shadow: 0 0 0 0 rgba(0, 70, 108, 0.15);
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease-in;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.bottom-item-buttons {
  align-content: right;
  align-items: right;
  text-align: right;
}

.modal-body {
  min-height: 0dvh;
}

.logo {
  text-align: center;
  margin-top: 1em;
  img {
    max-height: 100px;
    max-width: 50%;
  }
}
</style>
